import { Graphics, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "../gui/fader"
import { DyanmicTextContainer } from "../gui/dynamic_text_container"
import { Callback } from "game-common/util"
import { COLORS, INTRO_FONT } from "./intro_theme"

const LOGO_SIZE = 88
/** Wide tracking -- roughly 0.16em, applied between letters (never after the last one). */
const TRACKING = 14
const LETTER_DELAY = 150

export class ProjectLogo extends Graphics {
    logoText: string = "samsara"

    private letterWidths: number[] = []
    private totalWidth: number = 0

    constructor() {
        super()
        this.measure()
    }

    /**
     * Measure each letter exactly as it will be drawn -- one Text per letter, so no kerning between
     * them -- and cache the run's width.
     *
     * The old version hardcoded `calcWidth() => 630` against a wordmark that rendered ~427 wide, and
     * the letters were appended one per timer tick, so the container's width grew during the
     * animation. Anything positioned from it therefore moved while the logo was still spelling
     * itself out.
     */
    private measure = () => {
        const letters = this.logoText.split("")
        this.letterWidths = letters.map(letter => {
            const probe = this.renderLetter(letter, 0xffffff)
            const w = probe.width
            probe.destroy({ children: true, texture: true, baseTexture: true })
            return w
        })
        this.totalWidth =
            this.letterWidths.reduce((sum, w) => sum + w, 0) + TRACKING * Math.max(0, this.letterWidths.length - 1)
    }

    private renderLetter = (letter: string, color: number): Text => {
        const text = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .fontSize(LOGO_SIZE)
            .color(color)
            .render(letter) as Text
        // Set before anything reads .width: a style change re-rasterizes, and Jost 300 and 400 have
        // different advances, so measuring at the default weight would mis-size the run.
        text.style.fontWeight = "300"
        return text
    }

    makeLetter = (letter: string, fade: boolean = true, color: number = 0xffffff): Graphics => {
        const graphics = new Graphics()
        const r = this.renderLetter(letter, color)

        if (fade) {
            r.alpha = 0.01

            new Fader(r as any, {
                direction: "in",
                minAlpha: 1.0,
                fadeAlphaDecrement: 0.002,
            }).update()
        } else {
            r.alpha = 1.0
        }

        graphics.addChild(r)

        return graphics
    }

    calcWidth = () => this.totalWidth

    /**
     * Both passes -- the accent-green spell-out and the white copy that cross-fades over it -- are
     * built in full up front and revealed on a stagger, so the wordmark occupies its final width
     * from the first frame.
     */
    private buildRun = (color: number, fade: boolean, onLetter?: (index: number, letter: Graphics) => void) => {
        const container = new DyanmicTextContainer()
        let x = 0

        this.logoText.split("").forEach((letter, i) => {
            const rendered = this.makeLetter(letter, fade, color)
            rendered.x = x
            rendered.y = 0
            container.addChild(rendered)
            x += this.letterWidths[i] + TRACKING
            if (onLetter) {
                onLetter(i, rendered)
            }
        })

        return container
    }

    update = (callback: Callback<void>) => {
        const megaContainer = new DyanmicTextContainer()

        const letters = this.logoText.split("")

        const container = this.buildRun(COLORS.accent, true, (i, letter) => {
            letter.visible = false
            setTimeout(() => {
                letter.visible = true
            }, i * LETTER_DELAY)
        })
        megaContainer.append2(container, { exactLocation: { x: 0, y: 0 } })

        setTimeout(
            () => {
                new Fader(container).update()
            },
            LETTER_DELAY * 1.6 + letters.length * (LETTER_DELAY * 1.6),
        )

        const startFadeTs = letters.length * LETTER_DELAY

        setTimeout(() => {
            callback()
        }, startFadeTs + 100)

        setTimeout(() => {
            const white = this.buildRun(0xffffff, false)
            white.alpha = 0.01
            megaContainer.append2(white, { exactLocation: { x: 0, y: 0 } })

            new Fader(white, {
                direction: "in",
                minAlpha: 1.0,
                fadeAlphaDecrement: 0.002,
            }).update()
        }, startFadeTs)

        return megaContainer
    }
}
